import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  Error as ErrorIcon,
  CleaningServices as CleanupIcon,
} from "@mui/icons-material";
import Interceptor from "../../services/Interceptor";
import { SPACING } from "../../theme/constants";

const AdminContainers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState([]);
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await Interceptor.get("/workers/containers/utilization");
      setData(res.data?.data || null);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to fetch container utilization",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelect = (siteId) => {
    setSelected((prev) =>
      prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId],
    );
  };

  const handleSelectAll = (sites) => {
    const allIds = sites.map((s) => s.id);
    const allSelected = allIds.every((id) => selected.includes(id));
    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const res = await Interceptor.post("/workers/containers/cleanup", {
        siteIds: selected,
      });
      setCleanupResult(res.data);
      setSelected([]);
      fetchData();
    } catch (err) {
      setCleanupResult({
        success: false,
        message: err.response?.data?.error || "Cleanup failed",
      });
    } finally {
      setCleaning(false);
      setCleanupDialog(false);
    }
  };

  const statusColor = (status) => {
    const map = {
      ACTIVE: "success",
      PENDING: "warning",
      MAINTENANCE: "error",
      DELETED: "default",
      SUSPENDED: "info",
    };
    return map[status] || "default";
  };

  const SiteTable = ({ sites, selectable }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    sites.length > 0 &&
                    sites.every((s) => selected.includes(s.id))
                  }
                  indeterminate={
                    sites.some((s) => selected.includes(s.id)) &&
                    !sites.every((s) => selected.includes(s.id))
                  }
                  onChange={() => handleSelectAll(sites)}
                />
              </TableCell>
            )}
            <TableCell>Site</TableCell>
            <TableCell>Domain</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Environment</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Worker</TableCell>
            <TableCell>Created</TableCell>
            {sites[0]?.reason && <TableCell>Reason</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sites.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={selectable ? 9 : 8}
                align="center"
                sx={{ py: 3 }}
              >
                <Typography color="text.secondary">No sites found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sites.map((site) => (
              <TableRow key={site.id} hover>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(site.id)}
                      onChange={() => handleSelect(site.id)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {site.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {site.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{site.domain}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={site.status}
                    size="small"
                    color={statusColor(site.status)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={site.environment}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {site.user?.email || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {site.worker?.name || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(site.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                {site.reason && (
                  <TableCell>
                    <Typography
                      variant="caption"
                      color="error.main"
                      fontWeight={500}
                    >
                      {site.reason}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const WorkerResources = ({ workers }) => (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
      {workers?.map((w) => (
        <Card key={w.id} sx={{ flex: "1 1 280px", minWidth: 280 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {w.name}
              </Typography>
              <Chip
                label={w.status}
                size="small"
                color={w.status === "ACTIVE" ? "success" : "error"}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {w.ipAddress}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption">
                  Sites: {w.currentSites}/{w.maxSites}
                </Typography>
                <Typography variant="caption">
                  {Math.round((w.currentSites / w.maxSites) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((w.currentSites / w.maxSites) * 100, 100)}
                color={w.currentSites / w.maxSites > 0.8 ? "error" : "primary"}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  CPU: {w.cpuUsage?.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  RAM: {w.memoryUsage?.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Disk: {w.diskUsage?.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box>
      <Box
        sx={{
          mb: SPACING.lg,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            Container Utilization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor container resources and clean up orphaned/failed sites
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {selected.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<CleanupIcon />}
              onClick={() => setCleanupDialog(true)}
            >
              Clean Up ({selected.length})
            </Button>
          )}
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {cleanupResult && (
        <Alert
          severity={cleanupResult.success ? "success" : "error"}
          sx={{ mb: 2 }}
          onClose={() => setCleanupResult(null)}
        >
          {cleanupResult.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {[
              {
                label: "Active",
                count: data.summary?.activeSites || 0,
                icon: <ActiveIcon />,
                color: "success.main",
              },
              {
                label: "Orphaned",
                count: data.summary?.orphanedSites || 0,
                icon: <WarningIcon />,
                color: "warning.main",
              },
              {
                label: "Failed",
                count: data.summary?.failedSites || 0,
                icon: <ErrorIcon />,
                color: "error.main",
              },
              {
                label: "Deleted",
                count: data.summary?.deletedSites || 0,
                icon: <DeleteIcon />,
                color: "text.secondary",
              },
            ].map((item) => (
              <Card key={item.label} sx={{ flex: "1 1 140px", minWidth: 140 }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: item.color }}>{item.icon}</Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {item.count}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Worker Resources */}
          {data.workers?.length > 0 && (
            <WorkerResources workers={data.workers} />
          )}

          {/* Tabs */}
          <Card>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
            >
              <Tab label={`Active (${data.active?.length || 0})`} />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Orphaned ({data.orphaned?.length || 0})
                    {(data.orphaned?.length || 0) > 0 && (
                      <Tooltip title="These sites have stale containers consuming resources">
                        <WarningIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
              <Tab label={`Failed (${data.failed?.length || 0})`} />
              <Tab label={`Deleted (${data.deleted?.length || 0})`} />
            </Tabs>
            <CardContent sx={{ p: 0 }}>
              {tab === 0 && <SiteTable sites={data.active || []} />}
              {tab === 1 && (
                <SiteTable sites={data.orphaned || []} selectable />
              )}
              {tab === 2 && <SiteTable sites={data.failed || []} selectable />}
              {tab === 3 && <SiteTable sites={data.deleted || []} selectable />}
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupDialog} onClose={() => setCleanupDialog(false)}>
        <DialogTitle>Confirm Cleanup</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{selected.length}</strong>{" "}
            site(s) and their containers, volumes, and database records. Users
            will be notified.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialog(false)} disabled={cleaning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCleanup}
            disabled={cleaning}
          >
            {cleaning ? "Cleaning..." : "Confirm Cleanup"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContainers;
